// Story scene components — full-bleed SVG backdrops shown in StoryViewer.
// First choice: per-story inline SVG generated by the backend (sanitised before
// caching). Falls back to a hand-illustrated scene matching the scene key, then
// to a generic persona-tinted backdrop.

import type { ReactElement } from 'react';
import type { Persona } from '@/lib/types';

interface StorySceneProps {
  scene: string;
  /** Inline SVG generated per-story; takes precedence over the scene key. */
  svg?: string;
  persona?: Persona;
}

export function StoryScene({ scene, svg, persona }: StorySceneProps) {
  if (svg) {
    return (
      <div
        // Server-side sanitised: rejects script, foreignObject, on* handlers,
        // javascript:, image href. Safe to inline.
        dangerouslySetInnerHTML={{ __html: svg }}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    );
  }
  const scenes: Record<string, ReactElement> = {
    'cafe-morning': <SceneCafeMorning />,
    'cafe-cat': <SceneCafeCat />,
    'mountain': <SceneMountain />,
    'tea': <SceneTea />,
    'books-quiet': <SceneBooksQuiet />,
    'empty-road': <SceneEmptyRoad />,
  };
  return scenes[scene] || (persona ? <SceneGeneric persona={persona} /> : null);
}

// Generic full-screen story backdrop — uses the persona's gradient and a stylized
// repetition of their place motif. Lets any persona have a visual story without
// requiring a hand-drawn 400x700 illustration per scene.
function SceneGeneric({ persona }: { persona: Persona }) {
  const wash = 'rgba(255,255,255,0.18)';
  const light = 'rgba(255,255,255,0.32)';
  const dark = 'rgba(26,22,20,0.18)';
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id={`sg-${persona.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={persona.grad[0]} />
          <stop offset="100%" stopColor={persona.grad[1]} />
        </linearGradient>
      </defs>
      <rect width="400" height="700" fill={`url(#sg-${persona.id})`} />

      {/* Soft horizon band */}
      <rect x="0" y="320" width="400" height="380" fill="rgba(0,0,0,0.06)" />

      {/* Scattered motif circles for depth */}
      <circle cx="60" cy="120" r="50" fill={wash} />
      <circle cx="340" cy="200" r="80" fill={wash} />
      <circle cx="80" cy="500" r="120" fill={wash} />
      <circle cx="350" cy="600" r="100" fill={light} />

      {/* Center seal — persona's last name character as a chop mark */}
      <g transform="translate(200, 350)">
        <rect x="-50" y="-50" width="100" height="100" rx="6" fill={dark} opacity="0.7" />
        <text
          x="0" y="14"
          fontSize="72"
          fontFamily="'Noto Serif SC', serif"
          fontWeight="900"
          fill={persona.grad[0]}
          textAnchor="middle"
        >
          {persona.name.charAt(persona.name.length - 1)}
        </text>
      </g>

      {/* Subtle vignette */}
      <radialGradient id={`vig-${persona.id}`} cx="50%" cy="50%" r="60%">
        <stop offset="60%" stopColor="transparent" />
        <stop offset="100%" stopColor="rgba(0,0,0,0.28)" />
      </radialGradient>
      <rect width="400" height="700" fill={`url(#vig-${persona.id})`} />
    </svg>
  );
}

function SceneCafeMorning() {
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id="cm-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD3C2" /><stop offset="100%" stopColor="#E94B3C" />
        </linearGradient>
      </defs>
      <rect width="400" height="700" fill="url(#cm-bg)" />
      <g stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" fill="none">
        <path d="M0 100 L400 100 M0 130 L400 130 M50 70 L50 160 M150 70 L150 160 M250 70 L250 160 M350 70 L350 160" />
      </g>
      {/* Sunlight beams */}
      <g opacity="0.35">
        <path d="M50 200 L120 700 L0 700 Z" fill="#FFE5DD" />
        <path d="M380 200 L320 700 L400 700 Z" fill="#FFE5DD" />
      </g>
      {/* Coffee cup centered */}
      <g transform="translate(200 380)">
        <ellipse cx="0" cy="60" rx="100" ry="14" fill="rgba(0,0,0,0.18)" />
        <path d="M-80 -100 L-72 50 Q-72 60 -60 60 L60 60 Q72 60 72 50 L80 -100 Z" fill="#FAF6F0" stroke="#1A1614" strokeWidth="3" />
        <ellipse cx="0" cy="-100" rx="80" ry="12" fill="#6B3A24" />
        <ellipse cx="0" cy="-105" rx="62" ry="9" fill="#FAF6F0" opacity="0.4" />
        <path d="M80 -70 Q120 -70 120 -30 Q120 10 80 10" stroke="#1A1614" strokeWidth="3" fill="none" />
        {/* steam */}
        <path d="M-30 -130 Q-40 -160 -30 -190 Q-20 -220 -30 -250" stroke="rgba(255,255,255,0.85)" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M0 -130 Q10 -160 0 -190 Q-10 -220 0 -250" stroke="rgba(255,255,255,0.85)" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M30 -130 Q40 -160 30 -190 Q20 -220 30 -250" stroke="rgba(255,255,255,0.7)" strokeWidth="3" fill="none" strokeLinecap="round" />
      </g>
      {/* foam art heart */}
      <path d="M180 280 Q180 268 190 268 Q200 268 200 280 Q200 268 210 268 Q220 268 220 280 Q220 295 200 308 Q180 295 180 280 Z" fill="#6B3A24" opacity="0.5" />
    </svg>
  );
}

function SceneCafeCat() {
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id="cc-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE5DD" /><stop offset="100%" stopColor="#FFD3C2" />
        </linearGradient>
      </defs>
      <rect width="400" height="700" fill="url(#cc-bg)" />
      {/* counter */}
      <rect x="0" y="500" width="400" height="200" fill="#3D332E" />
      <rect x="0" y="490" width="400" height="14" fill="#5C4A40" />
      {/* milk bottle behind */}
      <g transform="translate(80 420)">
        <rect x="-15" y="-30" width="30" height="80" rx="3" fill="#FAF6F0" />
        <rect x="-15" y="-30" width="30" height="10" fill="#E94B3C" />
      </g>
      {/* cat sitting on counter */}
      <g transform="translate(220 410)">
        {/* body */}
        <ellipse cx="0" cy="70" rx="60" ry="40" fill="#3D332E" />
        {/* head */}
        <circle cx="0" cy="0" r="50" fill="#3D332E" />
        {/* ears */}
        <path d="M-40 -28 L-30 -65 L-15 -32 Z" fill="#3D332E" />
        <path d="M40 -28 L30 -65 L15 -32 Z" fill="#3D332E" />
        <path d="M-32 -32 L-26 -52 L-20 -36 Z" fill="#FFB5A8" />
        <path d="M32 -32 L26 -52 L20 -36 Z" fill="#FFB5A8" />
        {/* eyes */}
        <ellipse cx="-18" cy="-3" rx="6" ry="9" fill="#C9A36A" />
        <ellipse cx="18" cy="-3" rx="6" ry="9" fill="#C9A36A" />
        <rect x="-21" y="-7" width="6" height="14" rx="2" fill="#1A1614" />
        <rect x="15" y="-7" width="6" height="14" rx="2" fill="#1A1614" />
        {/* nose */}
        <path d="M-5 14 L5 14 L0 22 Z" fill="#FFB5A8" />
        {/* mouth */}
        <path d="M0 22 Q-6 28 -10 25 M0 22 Q6 28 10 25" stroke="#1A1614" strokeWidth="2" fill="none" strokeLinecap="round" />
        {/* whiskers */}
        <path d="M-12 18 L-40 14 M-12 22 L-40 24 M12 18 L40 14 M12 22 L40 24" stroke="#FAF6F0" strokeWidth="1.5" />
        {/* tail */}
        <path d="M55 80 Q90 60 95 30 Q98 10 80 -5" stroke="#3D332E" strokeWidth="20" fill="none" strokeLinecap="round" />
      </g>
      {/* sparkles */}
      <g fill="#C9A36A">
        <path d="M120 200 l4 -4 l4 4 l-4 4 z" /><path d="M320 280 l5 -5 l5 5 l-5 5 z" />
        <path d="M340 180 l3 -3 l3 3 l-3 3 z" />
      </g>
    </svg>
  );
}

function SceneMountain() {
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id="mt-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2C3E40" /><stop offset="60%" stopColor="#5C8A6B" /><stop offset="100%" stopColor="#C8DBC2" />
        </linearGradient>
      </defs>
      <rect width="400" height="700" fill="url(#mt-bg)" />
      {/* moon */}
      <circle cx="320" cy="120" r="50" fill="#FAF6F0" opacity="0.95" />
      <circle cx="305" cy="105" r="50" fill="#2C3E40" opacity="0.4" />
      {/* clouds */}
      <path d="M40 200 Q90 180 140 200 Q190 190 230 210" stroke="rgba(255,255,255,0.5)" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M180 130 Q230 115 270 135" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {/* mountain layers */}
      <path d="M0 480 L80 380 L130 430 L200 340 L270 410 L340 360 L400 400 L400 700 L0 700 Z" fill="rgba(26,22,20,0.35)" />
      <path d="M0 540 L100 460 L180 510 L240 440 L310 490 L400 460 L400 700 L0 700 Z" fill="rgba(26,22,20,0.55)" />
      <path d="M0 620 L120 560 L220 600 L320 560 L400 590 L400 700 L0 700 Z" fill="rgba(26,22,20,0.75)" />
      {/* tiny pavilion silhouette */}
      <g transform="translate(200 470)" fill="#1A1614">
        <rect x="-12" y="0" width="24" height="20" />
        <path d="M-20 0 L0 -12 L20 0 Z" />
        <path d="M-16 -2 L-8 -8 L-8 -2 Z M16 -2 L8 -8 L8 -2 Z" />
      </g>
    </svg>
  );
}

function SceneTea() {
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <radialGradient id="te-bg" cx="0.5" cy="0.4" r="0.7">
          <stop offset="0%" stopColor="#3D4A40" /><stop offset="100%" stopColor="#1A1614" />
        </radialGradient>
      </defs>
      <rect width="400" height="700" fill="url(#te-bg)" />
      {/* candle/lamp glow */}
      <circle cx="100" cy="180" r="120" fill="#C9A36A" opacity="0.15" />
      <circle cx="100" cy="180" r="60" fill="#C9A36A" opacity="0.25" />
      {/* tea cup centered */}
      <g transform="translate(200 460)">
        <ellipse cx="0" cy="80" rx="120" ry="18" fill="rgba(0,0,0,0.4)" />
        {/* saucer */}
        <ellipse cx="0" cy="60" rx="110" ry="14" fill="#5C4A40" />
        <ellipse cx="0" cy="55" rx="100" ry="10" fill="#3D332E" />
        {/* cup */}
        <path d="M-65 -30 L-58 50 Q-58 60 -48 60 L48 60 Q58 60 58 50 L65 -30 Z" fill="#FAF6F0" />
        <ellipse cx="0" cy="-30" rx="65" ry="10" fill="#5C8A6B" />
        <ellipse cx="0" cy="-32" rx="50" ry="6" fill="#3D5942" />
        {/* steam */}
        <path d="M-25 -60 Q-32 -100 -25 -140 Q-18 -180 -25 -220" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M0 -60 Q7 -100 0 -140 Q-7 -180 0 -220" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M25 -60 Q32 -100 25 -140 Q18 -180 25 -220" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      </g>
      {/* calligraphy chars in corner */}
      <text x="50" y="90" fontFamily="Noto Serif SC, serif" fontSize="36" fontWeight="900" fill="#C9A36A" opacity="0.6">茶</text>
    </svg>
  );
}

function SceneBooksQuiet() {
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id="bq-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3D332E" /><stop offset="100%" stopColor="#1A1614" />
        </linearGradient>
      </defs>
      <rect width="400" height="700" fill="url(#bq-bg)" />
      {/* desk lamp glow */}
      <circle cx="200" cy="300" r="200" fill="#C9A36A" opacity="0.15" />
      <circle cx="200" cy="300" r="100" fill="#C9A36A" opacity="0.2" />
      {/* book stack centered */}
      <g transform="translate(200 480)">
        <ellipse cx="0" cy="80" rx="140" ry="14" fill="rgba(0,0,0,0.5)" />
        <rect x="-110" y="20" width="220" height="36" rx="3" fill="#5C8A6B" opacity="0.85" />
        <rect x="-100" y="-20" width="200" height="36" rx="3" fill="#D8467A" opacity="0.85" />
        <rect x="-90" y="-60" width="180" height="36" rx="3" fill="#C9A36A" opacity="0.85" />
        {/* spine details */}
        <line x1="-110" y1="38" x2="110" y2="38" stroke="rgba(0,0,0,0.4)" />
        <line x1="-100" y1="-2" x2="100" y2="-2" stroke="rgba(0,0,0,0.4)" />
      </g>
      {/* open notebook */}
      <g transform="translate(200 580)">
        <path d="M-90 0 L-90 -30 L0 -36 L90 -30 L90 0 Z" fill="#FAF6F0" opacity="0.95" />
        <line x1="0" y1="-36" x2="0" y2="0" stroke="#1A1614" />
        <line x1="-80" y1="-25" x2="-10" y2="-30" stroke="#1A1614" strokeWidth="0.8" opacity="0.4" />
        <line x1="-80" y1="-18" x2="-10" y2="-23" stroke="#1A1614" strokeWidth="0.8" opacity="0.4" />
        <line x1="10" y1="-30" x2="80" y2="-25" stroke="#1A1614" strokeWidth="0.8" opacity="0.4" />
      </g>
      {/* stars (loneliness) */}
      <g fill="#FAF6F0" opacity="0.45">
        <circle cx="60" cy="80" r="1.5" /><circle cx="340" cy="60" r="2" />
        <circle cx="100" cy="140" r="1.5" /><circle cx="320" cy="180" r="1.5" />
      </g>
    </svg>
  );
}

function SceneEmptyRoad() {
  return (
    <svg viewBox="0 0 400 700" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <linearGradient id="er-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5C4F48" /><stop offset="60%" stopColor="#3D332E" /><stop offset="100%" stopColor="#1A1614" />
        </linearGradient>
      </defs>
      <rect width="400" height="700" fill="url(#er-bg)" />
      {/* distant hills */}
      <path d="M0 380 L100 340 L200 360 L300 330 L400 360 L400 700 L0 700 Z" fill="rgba(26,22,20,0.6)" />
      {/* road */}
      <path d="M180 700 L220 700 L260 380 L140 380 Z" fill="#1A1614" />
      {/* road dashes */}
      <g fill="#FAF6F0" opacity="0.6">
        <rect x="195" y="650" width="10" height="20" />
        <rect x="196" y="580" width="8" height="16" />
        <rect x="197" y="510" width="6" height="14" />
        <rect x="198" y="450" width="4" height="10" />
        <rect x="199" y="410" width="2" height="6" />
      </g>
      {/* lone street lamp */}
      <g transform="translate(310 350)">
        <line x1="0" y1="0" x2="0" y2="220" stroke="#1A1614" strokeWidth="3" />
        <path d="M-25 0 L-15 -15 L15 -15 L25 0 Z" fill="#1A1614" />
        <ellipse cx="0" cy="0" rx="90" ry="40" fill="#C9A36A" opacity="0.18" />
      </g>
      {/* stars */}
      <g fill="#FAF6F0" opacity="0.5">
        <circle cx="50" cy="60" r="1.5" /><circle cx="120" cy="100" r="1.5" />
        <circle cx="270" cy="80" r="1.5" /><circle cx="340" cy="160" r="1.5" />
        <circle cx="80" cy="180" r="1" />
      </g>
    </svg>
  );
}


